/**
 * Gradient's two palettes. This file is the single source of truth: the
 * layout turns it into CSS custom properties, and canvas / three.js widgets
 * read the same values through useGdColors().
 *
 * Colours carry meaning across the whole course and never swap roles:
 *   blue   → inputs (x)            green  → parameters / weights (w, b)
 *   amber  → outputs & targets (y) pink   → errors, losses & gradients
 *   violet → probabilities         accent → "you are here" / calls to action
 */

export type ThemeName = 'paper' | 'night'

export type Palette = {
  bg: string
  bg2: string
  surface: string
  ink: string
  ink2: string
  ink3: string
  rule: string
  rule2: string
  accent: string
  accentInk: string
  blue: string
  green: string
  amber: string
  pink: string
  violet: string
  codeBg: string
  grid: string
}

export const PALETTES: Record<ThemeName, Palette> = {
  paper: {
    bg: '#f3eee4',
    bg2: '#ebe4d6',
    surface: '#fbf8f2',
    ink: '#1a1814',
    ink2: '#4a453c',
    ink3: '#857d6f',
    rule: '#ddd4c3',
    rule2: '#c4b9a4',
    accent: '#e5482a',
    accentInk: '#fffaf3',
    blue: '#2a62d8',
    green: '#0e8f63',
    amber: '#c27f00',
    pink: '#d42a6f',
    violet: '#7445d6',
    codeBg: '#fbf8f2',
    grid: 'rgba(26, 24, 20, 0.07)',
  },
  night: {
    bg: '#111110',
    bg2: '#181816',
    surface: '#1c1b18',
    ink: '#eee9df',
    ink2: '#b3ac9e',
    ink3: '#7a7468',
    rule: '#2b2925',
    rule2: '#3e3b35',
    accent: '#ff5c3a',
    accentInk: '#140805',
    blue: '#6f9dff',
    green: '#38d39b',
    amber: '#fbbf3c',
    pink: '#ff6fa8',
    violet: '#ad8cff',
    codeBg: '#141412',
    grid: 'rgba(238, 233, 223, 0.06)',
  },
}

const VAR_NAMES: Record<keyof Palette, string> = {
  bg: '--gd-bg',
  bg2: '--gd-bg-2',
  surface: '--gd-surface',
  ink: '--gd-ink',
  ink2: '--gd-ink-2',
  ink3: '--gd-ink-3',
  rule: '--gd-rule',
  rule2: '--gd-rule-2',
  accent: '--gd-accent',
  accentInk: '--gd-accent-ink',
  blue: '--gd-blue',
  green: '--gd-green',
  amber: '--gd-amber',
  pink: '--gd-pink',
  violet: '--gd-violet',
  codeBg: '--gd-code-bg',
  grid: '--gd-grid',
}

function block(p: Palette): string {
  return (Object.keys(VAR_NAMES) as (keyof Palette)[]).map(k => `${VAR_NAMES[k]}:${p[k]};`).join('')
}

/** CSS for both themes, injected once by the Gradient layout. */
export function paletteCss(): string {
  return [
    `.gd-root{${block(PALETTES.paper)}color-scheme:light;}`,
    `.gd-root[data-theme="night"]{${block(PALETTES.night)}color-scheme:dark;}`,
    `body:has(.gd-root){background:${PALETTES.paper.bg};}`,
    `body:has(.gd-root[data-theme="night"]){background:${PALETTES.night.bg};}`,
  ].join('\n')
}

export const THEME_STORAGE_KEY = 'gd:theme'

/**
 * Runs before first paint (inline in the layout) so a night-mode reader never
 * sees a flash of paper. Mirrors the logic in ThemeProvider.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var r=document.currentScript&&document.currentScript.parentElement;if(!r)return;var t=localStorage.getItem('${THEME_STORAGE_KEY}');if(t!=='paper'&&t!=='night'){t=window.matchMedia('(prefers-color-scheme: dark)').matches?'night':'paper'}r.setAttribute('data-theme',t)}catch(e){}})();`

/** Hex "#rrggbb" + alpha → rgba() string, for canvas fills. */
export function withAlpha(hex: string, alpha: number): string {
  if (!hex.startsWith('#') || hex.length !== 7) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
